/**
 * Legalix Connect — partner-integration endpoints (webapp side).
 * Serves the public /integrations/authorize approve page and the protected
 * /integrations connections-management page. Partner-side endpoints
 * (pairing/token/shares) are NOT called from the webapp.
 */
import { backendApi } from "./backendApi.rtkq";

export interface PairingSessionInfo {
  partnerName: string;
  partnerSlug: string;
  deviceName: string | null;
  userCode: string;
  status: "pending" | "approved" | "denied" | "expired" | "consumed";
  expiresAt: string;
  precheck: {
    hasOffice: boolean;
    planEntitled: boolean;
  };
}

export interface IntegrationConnection {
  id: string;
  partnerSlug: string;
  partnerName: string;
  deviceName: string | null;
  tokenPrefix: string;
  status: "active" | "revoked";
  pairedUserName: string;
  pairedUserEmail: string;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

export const integrationsApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getPairingSession: builder.query<PairingSessionInfo, string>({
      query: (userCode) => `/api/integrations/pairing-sessions/${encodeURIComponent(userCode)}`,
    }),

    approvePairing: builder.mutation<{ status: string }, string>({
      query: (userCode) => ({
        url: `/api/integrations/pairing-sessions/${encodeURIComponent(userCode)}/approve`,
        method: "POST",
      }),
    }),

    denyPairing: builder.mutation<{ status: string }, string>({
      query: (userCode) => ({
        url: `/api/integrations/pairing-sessions/${encodeURIComponent(userCode)}/deny`,
        method: "POST",
      }),
    }),

    getConnections: builder.query<{ connections: IntegrationConnection[] }, void>({
      query: () => "/api/integrations/connections",
      providesTags: ["Integrations"],
    }),

    revokeConnection: builder.mutation<{ revoked: boolean }, string>({
      query: (connectionId) => ({
        url: `/api/integrations/connections/${connectionId}/revoke`,
        method: "POST",
      }),
      invalidatesTags: ["Integrations"],
    }),
  }),
});

export const {
  useGetPairingSessionQuery,
  useApprovePairingMutation,
  useDenyPairingMutation,
  useGetConnectionsQuery,
  useRevokeConnectionMutation,
} = integrationsApi;
